import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { usersApi } from '../api/users';
import { authApi } from '../api/auth';
import { Bell, Check, X, Trophy, ShoppingBag, ArrowLeftRight, Info, AlertTriangle } from 'lucide-react';
import { useLanguage } from '../i18n/LanguageContext';
import { formatDate } from '../utils/locale';
import { refreshBalance } from '../hooks/useBalance';

// Mappa actionUrl del backend alle route del frontend (es. /wallet -> /deposit, /notifications -> profile con tab)
const normalizeNotificationActionUrl = (url) => {
  if (!url || typeof url !== 'string') return null;
  const path = url.trim();
  if (path.startsWith('http://') || path.startsWith('https://')) return { type: 'external', url: path };
  if (path === '/wallet') return { type: 'internal', path: '/deposit' };
  if (path === '/notifications') return { type: 'internal', path: '/profile', state: { openTab: 'notifications' } };
  if (path.startsWith('/')) return { type: 'internal', path };
  return null;
};

const NotificationsDropdown = () => {
  const navigate = useNavigate();
  const [isOpen, setIsOpen] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const dropdownRef = useRef(null);
  const prevUnreadRef = useRef(-1);
  const notificationsInitialFetchDone = useRef(false);
  const user = authApi.getCurrentUser();
  const { t, locale } = useLanguage();

  const getBalanceUserId = () => {
    const u = authApi.getCurrentUser();
    if (!u?.id) return null;
    return u.isAdmin && u.linkedUserId ? u.linkedUserId : u.id;
  };

  useEffect(() => {
    if (user?.id) {
      notificationsInitialFetchDone.current = false;
      prevUnreadRef.current = -1;
      loadNotifications();
      // Refresh notifications every 30 seconds
      const interval = setInterval(loadNotifications, 30000);
      return () => clearInterval(interval);
    }
  }, [user?.id]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const loadNotifications = async () => {
    if (!user?.id) return;
    
    setLoading(true);
    try {
      const response = await usersApi.getNotifications(user.id, false, 20, 0);
      if (response?.success) {
        setNotifications(response.notifications || []);
        const unread = (response.notifications || []).filter((n) => !n.isRead).length;
        setUnreadCount(unread);
        const balanceUserId = getBalanceUserId();
        if (balanceUserId) {
          if (!notificationsInitialFetchDone.current) {
            notificationsInitialFetchDone.current = true;
            refreshBalance(balanceUserId);
          } else if (unread > prevUnreadRef.current) {
            refreshBalance(balanceUserId);
          }
        }
        prevUnreadRef.current = unread;
      }
    } catch (error) {
      console.error('Error loading notifications:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleMarkAsRead = async (notificationId: number) => {
    if (!user?.id) return;
    
    try {
      await usersApi.markNotificationAsRead(user.id, notificationId);
      setNotifications(notifications.map((n: any) => 
        n.id === notificationId ? { ...n, isRead: true } : n
      ));
      setUnreadCount(Math.max(0, unreadCount - 1));
    } catch (error) {
      console.error('Error marking notification as read:', error);
    }
  };

  const handleMarkAllAsRead = async () => {
    if (!user?.id) return;
    
    try {
      await usersApi.markAllNotificationsAsRead(user.id);
      setNotifications(notifications.map((n: any) => ({ ...n, isRead: true })));
      setUnreadCount(0);
    } catch (error) {
      console.error('Error marking all notifications as read:', error);
    }
  };

  const getIcon = (type: string) => {
    switch (type) {
      case 'prize': return <Trophy className="w-5 h-5" />;
      case 'tournament': return <Trophy className="w-5 h-5" />;
      case 'trade': return <ArrowLeftRight className="w-5 h-5" />;
      case 'marketplace': return <ShoppingBag className="w-5 h-5" />;
      case 'warning': return <AlertTriangle className="w-5 h-5" />;
      default: return <Info className="w-5 h-5" />;
    }
  };

  const formatRelativeDate = (dateString: string) => {
    const date = new Date(dateString);
    const now = new Date();
    const diffMs = now.getTime() - date.getTime();
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return t('notifications.justNow');
    if (diffMins < 60) return t('notifications.minutesAgo', { count: diffMins });
    if (diffHours < 24) return t('notifications.hoursAgo', { count: diffHours });
    if (diffDays < 7) return t('notifications.daysAgo', { count: diffDays });
    return formatDate(date, {}, locale);
  };

  if (!user?.id) {
    return null;
  }

  return (
    <div className="relative gaming-navbar-notifications" ref={dropdownRef}>
      <button
        onClick={() => {
          const nextOpen = !isOpen;
          setIsOpen(nextOpen);
          if (nextOpen) {
            const balanceUserId = getBalanceUserId();
            if (balanceUserId) {
              refreshBalance(balanceUserId);
            }
            loadNotifications();
          }
        }}
        className="relative p-2 text-gray-300 hover:text-white transition-colors overflow-visible"
        aria-label={t('notifications.ariaLabel')}
      >
        <Bell className="w-6 h-6" />
        {unreadCount > 0 && (
          <span className="gaming-notification-badge bg-red-500 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {isOpen && (
        <div className="gaming-notifications-dropdown absolute right-0 mt-2 w-[min(24rem,calc(100vw-1rem))] max-w-[calc(100vw-1rem)] bg-gray-800 rounded-lg shadow-xl border border-gray-700 z-50 max-h-96 flex flex-col">
          {/* Due righe: evita che "Segna tutte come lette" sia compressa in una colonna stretta (taglio su una riga) */}
          <div className="gaming-notifications-dropdown-header shrink-0 px-4 py-3 border-b border-gray-700 flex flex-col gap-2">
            <h3 className="text-base font-bold text-white leading-tight">
              {t('notifications.title')}
            </h3>
            <button
              type="button"
              onClick={handleMarkAllAsRead}
              disabled={unreadCount === 0}
              className={`gaming-notifications-mark-all w-full text-left text-sm font-semibold leading-snug ${
                unreadCount === 0
                  ? 'text-white/50 cursor-not-allowed'
                  : 'text-white/90 hover:text-white hover:underline'
              }`}
            >
              {t('notifications.markAllRead')}
            </button>
          </div>

          <div className="gaming-notifications-dropdown-scroll overflow-y-auto flex-1 min-h-0 overscroll-contain">
            {loading ? (
              <div className="p-4 text-center text-gray-400">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-yellow-500 mx-auto"></div>
              </div>
            ) : notifications.length === 0 ? (
              <div className="p-4 text-center text-gray-400">
                <Bell className="w-8 h-8 mx-auto mb-2 opacity-50" />
                <p>{t('notifications.noNotifications')}</p>
              </div>
            ) : (
              <div className="divide-y divide-gray-700">
                {notifications.map((notification: any) => (
                  <div
                    key={notification.id}
                    className={`p-4 hover:bg-gray-700/50 transition-colors cursor-pointer ${
                      !notification.isRead ? 'bg-blue-500/10' : ''
                    }`}
                    onClick={() => {
                      if (!notification.isRead) {
                        handleMarkAsRead(notification.id);
                      }
                      const action = normalizeNotificationActionUrl(notification.actionUrl);
                      if (action?.type === 'internal') {
                        setIsOpen(false);
                        navigate(action.path, action.state ? { state: action.state } : {});
                      } else if (action?.type === 'external') {
                        window.location.href = action.url;
                      }
                    }}
                  >
                    <div className="flex items-start space-x-3">
                      <div className="mt-0.5 text-yellow-500">
                        {getIcon(notification.type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between">
                          <h4 className="font-semibold platform-notification-text text-sm mb-1">
                            {notification.title}
                          </h4>
                          {!notification.isRead && (
                            <div className="w-2 h-2 bg-blue-500 rounded-full mt-1 ml-2 flex-shrink-0"></div>
                          )}
                        </div>
                        <p className="text-gray-300 text-xs mb-1 line-clamp-2">
                          {notification.message}
                        </p>
                        <p className="text-gray-500 text-xs">
                          {formatRelativeDate(notification.createdAt)}
                        </p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default NotificationsDropdown;
